import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sun, Moon, Zap, ChevronLeft, ChevronRight, Search, Bookmark } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "@/components/Logo";
import SearchModal from "@/components/SearchModal";
import { CATEGORIES, fetchArticles, catLabel } from "@/lib/api";

const ThemeToggle = () => {
  const [light, setLight] = useState(() => localStorage.getItem("khabrik-theme") === "light");
  useEffect(() => {
    document.documentElement.classList.toggle("light", light);
    localStorage.setItem("khabrik-theme", light ? "light" : "dark");
  }, [light]);
  return (
    <button
      data-testid="theme-toggle-button"
      onClick={() => setLight(!light)}
      aria-label={light ? "डार्क मोड" : "लाइट मोड"}
      className="p-2.5 border border-white/15 text-slate-300 hover:text-[#FFD400] hover:border-[#FFD400] transition-colors duration-200"
    >
      {light ? <Moon size={16} /> : <Sun size={16} />}
    </button>
  );
};

const ROTATE_MS = 5000;

const Ticker = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    fetchArticles({ limit: 8 }).then(setItems).catch(() => {});
  }, []);

  const go = (dir) => setIdx((i) => (i + dir + items.length) % items.length);

  useEffect(() => {
    if (!items.length || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [items.length, paused]);

  if (!items.length) return null;
  const item = items[idx];

  return (
    <div
      data-testid="breaking-news-ticker"
      className="force-dark relative h-10 flex items-stretch overflow-hidden"
      style={{ background: "#0A0A0D", borderBottom: "1px solid rgba(255,212,0,0.25)" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex-shrink-0 flex items-center gap-2 px-3 sm:px-4 bg-[#FFD400] relative">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF453A] opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF453A]"></span>
        </span>
        <Zap size={13} className="text-[#0D0D10] hidden sm:block" fill="#0D0D10" />
        <span className="font-mono-meta text-[11px] font-semibold uppercase tracking-widest text-[#0D0D10]">
          ब्रेकिंग
        </span>
        <span
          className="absolute right-[-10px] top-0 bottom-0 w-[10px]"
          style={{ background: "#FFD400", clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
        />
      </div>

      <div className="flex-1 relative min-w-0 ml-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ y: 22, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -22, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 flex items-center gap-3 pr-4"
          >
            <span className="hidden md:inline font-mono-meta text-[9px] uppercase tracking-widest text-[#FFD400] border border-[#FFD400]/40 px-1.5 py-0.5 flex-shrink-0">
              {catLabel(item.category)}
            </span>
            <Link
              to={`/article/${item.id}`}
              data-testid="ticker-headline-link"
              className="text-sm font-semibold text-white hover:text-[#FFD400] transition-colors truncate"
            >
              {item.title}
            </Link>
          </motion.div>
        </AnimatePresence>
        {!paused && (
          <motion.span
            key={`bar-${idx}`}
            className="absolute bottom-0 left-0 h-[2px] bg-[#FFD400]"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: ROTATE_MS / 1000, ease: "linear" }}
          />
        )}
      </div>

      <div className="flex-shrink-0 flex items-center gap-1 px-2 sm:px-3 border-l border-white/10">
        <span className="font-mono-meta text-[10px] text-slate-400 mr-1 hidden sm:block">
          <span className="text-[#FFD400]">{String(idx + 1).padStart(2, "0")}</span>/{String(items.length).padStart(2, "0")}
        </span>
        <button
          data-testid="ticker-prev-button"
          onClick={() => go(-1)}
          aria-label="पिछली खबर"
          className="p-1 text-slate-400 hover:text-[#FFD400] transition-colors"
        >
          <ChevronLeft size={14} />
        </button>
        <button
          data-testid="ticker-next-button"
          onClick={() => go(1)}
          aria-label="अगली खबर"
          className="p-1 text-slate-400 hover:text-[#FFD400] transition-colors"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default function SiteHeader() {
  const { pathname } = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <header data-testid="main-header" className="fixed top-0 left-0 right-0 z-50">
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      <Ticker />
      <div className="backdrop-blur-xl bg-[#0D0D10]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1 overflow-x-auto no-scrollbar">
            {CATEGORIES.map((c) => {
              const active = pathname === `/category/${c.id}` || (pathname === "/" && c.id === "top-stories");
              return (
                <Link
                  key={c.id}
                  data-testid={`category-tab-${c.id}`}
                  to={c.id === "top-stories" ? "/" : `/category/${c.id}`}
                  className={`px-3 py-2 text-sm font-semibold transition-colors duration-200 border-b-2 ${
                    active
                      ? "text-[#FFD400] border-[#FFD400]"
                      : "text-slate-300 border-transparent hover:text-white hover:border-white/30"
                  }`}
                >
                  {c.hi}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            <button
              data-testid="search-open-button"
              onClick={() => setSearchOpen(true)}
              aria-label="खोजें"
              className="p-2.5 border border-white/15 text-slate-300 hover:text-[#FFD400] hover:border-[#FFD400] transition-colors duration-200"
            >
              <Search size={16} />
            </button>
            <Link
              to="/bookmarks"
              data-testid="bookmarks-link"
              aria-label="सहेजे गए लेख"
              className={`p-2.5 border transition-colors duration-200 ${
                pathname === "/bookmarks"
                  ? "border-[#FFD400] text-[#FFD400]"
                  : "border-white/15 text-slate-300 hover:text-[#FFD400] hover:border-[#FFD400]"
              }`}
            >
              <Bookmark size={16} />
            </Link>
            <ThemeToggle />
          </div>
        </div>
        <nav className="lg:hidden flex items-center gap-1 overflow-x-auto no-scrollbar px-4 pb-2">
          {CATEGORIES.map((c) => (
            <Link
              key={c.id}
              to={c.id === "top-stories" ? "/" : `/category/${c.id}`}
              className="px-3 py-1.5 text-xs font-semibold text-slate-300 whitespace-nowrap border border-white/10 hover:border-[#FFD400] hover:text-[#FFD400] transition-colors"
            >
              {c.hi}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
}
